import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button, Input, message } from "antd";
import moment from "moment";
import CryptoJS from "crypto-js";
import DatePicker from "react-bootstrap-daterangepicker"; // Import DatePicker
import "bootstrap/dist/css/bootstrap.min.css"; // Import Bootstrap CSS
import "bootstrap-daterangepicker/daterangepicker.css"; // Import DatePicker CSS
import { renderDate } from "./utils/dateFormatter";
import UserService from "./utils/userService";

const { Search } = Input;
const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";
const secretKey = "abde100199199111";

const GuestCardView = () => {
  // Fetch business_date from localStorage and parse it as a local moment (assuming local storage date is in local time)
  const businessDateFromStorage = localStorage.getItem("business_date");
  const businessMoment = businessDateFromStorage && moment(businessDateFromStorage).isValid()
    ? moment(businessDateFromStorage).startOf("day")
    : moment().startOf("day");

  // Initialize dateRange with business_date as a single-day range
  const [dateRange, setDateRange] = useState({
    startDate: businessMoment,
    endDate: businessMoment,
  });

  const [searchText, setSearchText] = useState("");
  const [guestData, setGuestData] = useState([]);
  const [loading, setLoading] = useState(false);
  const { getFullname } = UserService();

  useEffect(() => {
    fetchGuests(dateRange.startDate, dateRange.endDate);
  }, [dateRange]); // Trigger on dateRange changes

  const fetchGuests = async (startDate, endDate) => {
    // Validate dates before proceeding
    if (!startDate || !endDate || !moment(startDate).isValid() || !moment(endDate).isValid()) {
      message.error("Invalid date range provided.");
      setGuestData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const token = localStorage.getItem("token");

    try {
      // Send the date range in local time format (YYYY-MM-DD), adjusting for API if needed
      const startDateStr = startDate.format("YYYY-MM-DD");
      const endDateStr = endDate.format("YYYY-MM-DD");
      console.log("Fetching guests for range:", { startDateStr, endDateStr }); // Debug log
      const response = await fetch(
        `${apiURL}/booking/reservations?checkedout=${startDateStr}&enddate=${endDateStr}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      if (data.success) {
        setGuestData(
          data.reservations.map((guest) => ({
            ...guest,
            num_adults: guest.num_adults || 0,
            num_children: guest.num_children || 0,
            check_out_date: guest.check_out_date ? moment(guest.check_out_date).local().format("DD MMM YYYY") : "N/A",
          }))
        );
      } else {
        message.error("Failed to fetch reservations.");
        setGuestData([]);
      }
    } catch (error) {
      message.error("Error fetching reservations.");
      setGuestData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (param) => {
    setDateRange((prevRange) => {
      let newRange;
      if (typeof param === "string") {
        if (param === "prev") {
          newRange = {
            startDate: prevRange.startDate.clone().subtract(1, "day").startOf("day"),
            endDate: prevRange.endDate.clone().subtract(1, "day").startOf("day"),
          };
        } else if (param === "next") {
          newRange = {
            startDate: prevRange.startDate.clone().add(1, "day").startOf("day"),
            endDate: prevRange.endDate.clone().add(1, "day").startOf("day"),
          };
          if (newRange.startDate.isAfter(businessMoment)) {
            message.error("Cannot select future dates beyond the business date.");
            return prevRange;
          }
        } else {
          return prevRange;
        }
      } else {
        const { start, end } = param;
        const newStart = moment(start).startOf("day");
        const newEnd = moment(end).startOf("day");
        console.log("Selected range:", { newStart: newStart.format("YYYY-MM-DD"), newEnd: newEnd.format("YYYY-MM-DD") }); // Debug log

        if (newStart.isAfter(businessMoment) || newEnd.isAfter(businessMoment)) {
          message.error("Cannot select future dates beyond the business date.");
          return prevRange;
        }

        newRange = { startDate: newStart, endDate: newEnd };
      }

      // Validate dates before calling API
      if (!newRange.startDate.isValid() || !newRange.endDate.isValid()) {
        message.error("Invalid date selected.");
        setGuestData([]);
        return prevRange;
      }

      if (newRange.startDate.isAfter(newRange.endDate)) {
        message.error("Start date cannot be after end date.");
        setGuestData([]);
        return prevRange;
      }

      console.log("New range after validation:", { start: newRange.startDate.format("YYYY-MM-DD"), end: newRange.endDate.format("YYYY-MM-DD") }); // Debug log
      // Fetch guests with the new date range
      fetchGuests(newRange.startDate, newRange.endDate);

      return newRange;
    });
  };

  const encryptId = (id) => {
    return CryptoJS.AES.encrypt(id.toString(), secretKey).toString();
  };

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <h2 className="mb-0">Checked-Out Guests</h2>
            <div className="d-flex align-items-center gap-2">
              <DatePicker
                initialSettings={{
                  singleDatePicker: false,
                  showDropdowns: true,
                  startDate: dateRange.startDate.toDate(),
                  endDate: dateRange.endDate.toDate(),
                  maxDate: businessMoment.toDate(), // Restrict to business_date and past
                  ranges: {
                    "Today": [businessMoment.toDate(), businessMoment.toDate()],
                    "Yesterday": [
                      businessMoment.clone().subtract(1, "day").toDate(),
                      businessMoment.clone().subtract(1, "day").toDate(),
                    ],
                    "Last Week": [
                      businessMoment.clone().subtract(1, "week").startOf("week").toDate(),
                      businessMoment.clone().subtract(1, "week").endOf("week").toDate(),
                    ],
                    "Last Month": [
                      businessMoment.clone().subtract(1, "month").startOf("month").toDate(),
                      businessMoment.clone().subtract(1, "month").endOf("month").toDate(),
                    ],
                  },
                  locale: {
                    format: "DD MMM YYYY",
                    applyLabel: "Select",
                    cancelLabel: "Cancel",
                  },
                }}
                onCallback={(start, end) => {
                  handleDateChange({ start, end });
                }}
              >
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    value={`${dateRange.startDate.local().format("DD MMM YYYY")} - ${dateRange.endDate.local().format("DD MMM YYYY")}`}
                    style={{
                      width: "220px",
                      height: "38px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      textAlign: "center",
                      padding: "0 10px 0 10px",
                      borderRight: "none",
                      backgroundColor: "#ffffff",
                    }}
                    readOnly
                  />
                  <div className="input-group-append">
                    <span
                      className="input-group-text"
                      style={{
                        height: "38px",
                        backgroundColor: "#fff",
                        borderLeft: "none",
                        borderRadius: "0 4px 4px 0",
                        cursor: "pointer",
                      }}
                    >
                      <i className="fa fa-chevron-down" style={{ color: "#495057" }} />
                    </span>
                  </div>
                </div>
              </DatePicker>
            </div>
            <Search
              placeholder="Search by Name, Email, or Booking ID"
              allowClear
              enterButton="Search"
              size="large"
              onChange={(e) => setSearchText(e.target.value)}
              style={{ maxWidth: 450 }}
            />
            <div className="ms-auto">
              <Link to="/hospitality/guest-reservation" className="btn add-btn custom-btn">
                <i className="la la-plus-circle custom-icon" /> Add New Reservation
              </Link>
            </div>
          </div>
        </div>

        {loading ? (
          <div id="loader-wrapper-">
            <div className="loader">
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
              <div className="dot"></div>
            </div>
          </div>
        ) : (
          <div className="row">
            {guestData.length > 0 ? (
              guestData
                .filter((guest) =>
                  guest.guest_name.toLowerCase().includes(searchText.toLowerCase())
                )
                .map((guest) => (
                  <div
                    className="col-md-4 col-sm-6 col-12 col-lg-3 col-xl-3"
                    key={guest.booking_id}
                  >
                    <div
                      className="profile-bg shadow-sm rounded p-3 position-relative"
                      style={{ marginBottom: "15px" }}
                    >
                      <div className="d-flex align-items-center mt-1">
                        <i
                          className="fas fa-user-circle me-2"
                          style={{ color: "#2d2e83" }}
                        ></i>
                        <h5 className="user-name">
                          <Link
                            to={`/hospitality/reservation-details/${encodeURIComponent(
                              encryptId(guest.booking_id)
                            )}`}
                          >
                            {guest.guest_name}
                          </Link>
                        </h5>
                      </div>
                      <div className="profile-details mt-2">
                        <p className="d-flex align-items-center">
                              <i className="fas fa-calendar-alt me-2" style={{ color: "#2d2e83", fontSize: "16px" }}></i>
    <span className="rounded bg-success"  style={{ padding: "3px 10px" }}>
      {guest?.check_in_date && guest?.check_out_date
        ? `${renderDate(guest.check_in_date)} - ${renderDate(guest.check_out_date)}`
        : "N/A"}
    </span>
                        </p>
                      </div>
                      <ul className="profile-details">
                        <li>
                          <i
                            className="fas fa-envelope me-2"
                            style={{ color: "#2d2e83" }}
                          ></i>
                          <a
                            href={`mailto:${guest.guest_email}`}
                            className="contact-link"
                          >
                            {guest.guest_email || "N/A"}
                          </a>
                        </li>
                      </ul>
                      <ul className="profile-details">
                        <li>
                          <i
                            className="fas fa-bed me-2"
                            style={{ color: "#2d2e83" }}
                          ></i>
                          {guest.room_types} - {guest.room_numbers}
                        </li>
                        <li>
                          <i
                            className="fas fa-users me-2"
                            style={{ color: "#2d2e83" }}
                          ></i>
                          {guest.num_adults || 0}{" "}
                          <i className="fas fa-male ms-1 text-primary"></i>,
                          {guest.num_children || 0}{" "}
                          <i className="fas fa-child ms-1 text-warning"></i>
                        </li>


                             <li>
                          <i
                            className="fas fa-user me-2"
                            style={{ color: "#2d2e83" }}
                          ></i>
                         Checked Out By :
                          {getFullname(guest.checked_out_by)  }{" "}
                     </li>
                      </ul>

                    </div>
                  </div>
                ))
            ) : (
              <div className="no-data-container text-center mt-4">
                <img
                  src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                  alt="No data available"
                  style={{ width: "150px", marginBottom: "20px" }}
                />
                <h3>No Data Available</h3>
                <p>No guests have checked out in this date range.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestCardView;